/**
 * iter-338v (BOQEditPage Refactor Phase A):
 * Publish-to-{Client|Vendor} validity picker with 3/7/14/20/30/60/90 day
 * quick-picks + custom input. Extracted verbatim from BOQEditPage.jsx.
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Share2, Users } from 'lucide-react';

const QUICK_DAYS = [3, 7, 14, 20, 30, 60, 90];

export default function PublishValidityDialog({
  open,
  onOpenChange,
  target,          // 'client' | 'vendor'
  validityDays,
  onValidityChange,
  publishing,
  publishingVendor,
  onPublishClient,
  onPublishVendor,
  worksheet,
  // iter-386u-f — task #6: Display in Dashboards toggle.
  displayInDashboards = true,
  onDisplayInDashboardsChange,
}) {
  const isClient = target === 'client';
  const daysInt = parseInt(validityDays) || 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="publish-validity-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isClient
              ? <Share2 size={18} className="text-blue-600" />
              : <Users size={18} className="text-purple-600" />}
            Publish to {isClient ? 'Client' : 'Vendor'} — Set Validity
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className={`p-3 rounded-lg border text-sm ${
            isClient
              ? 'bg-blue-50 border-blue-200 text-blue-900'
              : 'bg-purple-50 border-purple-200 text-purple-900'
          }`}>
            <strong>{worksheet?.worksheet_number || 'BOQ'}</strong> — choose how long the public link will stay valid.
          </div>
          <div>
            <Label className="text-sm font-medium text-stone-700 mb-2 block">Quick picks</Label>
            <div className="flex flex-wrap gap-2" data-testid="publish-validity-quick-picks">
              {QUICK_DAYS.map((days) => {
                const active = validityDays === days;
                return (
                  <button
                    key={days}
                    type="button"
                    onClick={() => onValidityChange(days)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                      active
                        ? isClient
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-purple-600 text-white border-purple-600'
                        : 'bg-white text-stone-700 border-stone-200 hover:bg-stone-50'
                    }`}
                    data-testid={`publish-validity-${days}d`}
                  >
                    {days} days
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <Label htmlFor="custom-validity-days" className="text-sm font-medium text-stone-700">Custom (days)</Label>
            <Input
              id="custom-validity-days"
              type="number"
              min="1"
              max="365"
              value={validityDays}
              onChange={(e) => onValidityChange(parseInt(e.target.value) || 0)}
              className="mt-1 w-32"
              data-testid="publish-validity-custom-input"
            />
            <p className="text-xs text-stone-500 mt-1">
              Link will expire on {new Date(Date.now() + daysInt * 86400000).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
            </p>
          </div>
          {/* iter-386u-f — task #6: Display in Dashboards toggle */}
          {isClient && (
            <label className="flex items-center gap-2 pt-2 border-t border-stone-100 cursor-pointer" data-testid="publish-display-in-dashboards-wrap">
              <input
                type="checkbox"
                checked={!!displayInDashboards}
                onChange={(e) => onDisplayInDashboardsChange && onDisplayInDashboardsChange(e.target.checked)}
                className="h-4 w-4 accent-blue-600"
                data-testid="publish-display-in-dashboards"
              />
              <span className="text-sm text-stone-700">
                Display in <strong>My BOQ</strong> dashboards for the client and assigned vendors
              </span>
            </label>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={isClient ? onPublishClient : onPublishVendor}
            disabled={publishing || publishingVendor || !validityDays}
            className={isClient ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-purple-600 hover:bg-purple-700 text-white'}
            data-testid="publish-validity-confirm-btn"
          >
            {(publishing || publishingVendor) ? 'Publishing…' : `Publish (${validityDays}d)`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
